import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import {
    AbstractControl,
    UntypedFormBuilder,
    UntypedFormGroup,
    ValidationErrors,
    ValidatorFn,
    Validators,
} from '@angular/forms';
import { of, Subscription } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { NotificationType } from '../../shared/models/notification-type';
import { ErrorHandlerService } from '../../shared/services/error-handler/error-handler.service';
import { NotificationService } from '../../shared/services/notification/notification.service';
import { UtilityService } from '../../shared/services/utility/utility.service';
import { INotifyTemplate } from '../models/notify-template.interface';
import { NotifyTemplateService } from '../services/notify-template.service';
import { TranslateService } from '@ngx-translate/core';
import { User } from '../../shared/services/user/models/user.model';
import { AppState } from 'app/app.state';

@Component({
    selector: 'upsc-notify-template-form',
    templateUrl: './notify-template-form.component.html',
    styleUrls: ['./notify-template-form.component.scss'],
})
export class NotifyTemplateFormComponent implements OnInit, OnDestroy {
    @Input() template: INotifyTemplate;
    @Output() formValueChanged: EventEmitter<INotifyTemplate> = new EventEmitter<INotifyTemplate>();
    @Output() templateCreated: EventEmitter<INotifyTemplate> = new EventEmitter<INotifyTemplate>();

    @ViewChild('coverImageInput') public coverImageInput: ElementRef;
    @ViewChild('bannerImageInput') public bannerImageInput: ElementRef;
    @ViewChild('showcaseImageInput') public showcaseImageInput: ElementRef;

    public formGroup: UntypedFormGroup;
    public isInProgress = false;
    public isAddShowcasePhotosEnabled = false;
    public coverImageFileName;
    public bannerImageFileName;
    public showcaseImageFileNames = [];
    public addNotifyTemplateSubscription: Subscription;
    public isCoverImageUploading = false;
    public isBannerImageUploading = false;
    public isShowcaseImageUploading = false;
    public isCoverImageValid = true;
    public isBannerImageValid = true;
    public isShowcaseImageValid = true;
    public sessionLanguage : string;
    public termsOfUseURL: string;
    public user: User;

    constructor(private formBuilder: UntypedFormBuilder,
                private notificationService: NotificationService,
                private utilityService: UtilityService,
                private errorHandlerService: ErrorHandlerService,
                private notifyTemplateService: NotifyTemplateService,
                private translate : TranslateService,
                private readonly appState: AppState,
    ) {
        this.setupForm();
        this.translate.onLangChange.subscribe((event) => {
            this.sessionLanguage = event.lang.toUpperCase();
        })
    }

    public ngOnInit() {
        if (this.template) {
            const hasShowcaseImage = !!this.template.ShowCaseImg1S3Url || !!this.template.ShowCaseImg2S3Url || !!this.template.ShowCaseImg3S3Url;
            this.formGroup.setValue({
                templateName: this.template.TemplateName,
                ppiTemplateName: this.template.PPITemplateName,
                websiteUrl: this.template.CustomerWebsite,
                coverImageUrl: this.template.CoverBannerS3Url,
                bannerImageUrl: this.template.BannerS3Url,
                shouldDisplayShowcaseImages: hasShowcaseImage,
                showcaseImageUrls: [this.template.ShowCaseImg1S3Url, this.template.ShowCaseImg2S3Url, this.template.ShowCaseImg3S3Url],
            });
        }
        this.sessionLanguage = sessionStorage.getItem('lang').toUpperCase();
        this.setTermsOfUseURL();
    }

    public ngOnDestroy() {
        if (this.addNotifyTemplateSubscription) {
            this.addNotifyTemplateSubscription.unsubscribe();
            this.addNotifyTemplateSubscription = null;
        }
    }

    private isAllowedFileExtensions(fileName: string, allowedFileExtensions: string[]): boolean {
        if (!fileName) {
            return false;
        }

        const extension = fileName.split('.').pop().toLowerCase();
        return allowedFileExtensions.includes(extension);
    }

    public onCoverImageSelected(event): void {
        const files = event.target.files;
        if (!files || files.length === 0) {
            return;
        }

        this.coverImageFileName = files[0].name;

        this.isCoverImageValid =this.isAllowedFileExtensions(this.coverImageFileName, ['jpg', 'jpeg', 'png']);
        if (!this.isCoverImageValid) {
            this.removeCoverImage(null);
            return;
        }

        this.isCoverImageUploading = true;
        this.notifyTemplateService.uploadTemplatePhoto(files, 'cover image')
            .pipe(
                catchError((err) => {
                    this.notificationService.notify(
                        this.errorHandlerService.getHttpErrorMessage(err),
                        'Failed uploading cover image',
                        NotificationType.ERROR);
                    this.isCoverImageUploading = false;

                    return of(null);
                }),
                finalize(() => {
                    this.coverImageInput.nativeElement.value = '';
                }),
            )
            .subscribe(
                (res) => {
                    if (res) {
                        this.formGroup.controls.coverImageUrl.setValue(res.toString().replace(/^"(.+(?="$))"$/, '$1'));
                    }
                    this.isCoverImageUploading = false;
                },
            );
    }

    public onBannerImageSelected(event): void {
        const files = event.target.files;
        if (!files || files.length === 0) {
            return;
        }

        this.bannerImageFileName = files[0].name;

        this.isBannerImageValid = this.isAllowedFileExtensions(this.bannerImageFileName, ['jpg', 'jpeg', 'png']);
        if (!this.isBannerImageValid) {
            this.removeBannerImage(null);
            return;
        }

        this.isBannerImageUploading = true;
        this.notifyTemplateService.uploadTemplatePhoto(files, 'banner image')
            .pipe(
                catchError((err) => {
                    this.notificationService.notify(
                        this.errorHandlerService.getHttpErrorMessage(err),
                        'Failed uploading banner image',
                        NotificationType.ERROR);
                    this.isBannerImageUploading = false;

                    return of(null);
                }),
                finalize(() => {
                    this.bannerImageInput.nativeElement.value = '';
                }),
            )
            .subscribe(
                (res) => {
                    if (res) {
                        this.formGroup.controls.bannerImageUrl.setValue(res.toString().replace(/^"(.+(?="$))"$/, '$1'));
                    }
                    this.isBannerImageUploading = false;
                },
            );
    }

    public onShowcaseImagesSelected(event): void {
        if (this.showcaseImageFileNames && this.showcaseImageFileNames.length >= 3) {
            return;
        }

        const files = event.target.files;
        if (!files || files.length === 0) {
            return;
        }

        const showcaseImageFileName = files[0].name;
        this.isShowcaseImageValid = this.isAllowedFileExtensions(showcaseImageFileName, ['jpg', 'jpeg', 'png']);
        if (!this.isShowcaseImageValid) {
            this.removeShowcaseImage(null, showcaseImageFileName);
            return;
        }

        this.showcaseImageFileNames.push(files[0].name);

        this.isShowcaseImageUploading = true;
        this.notifyTemplateService.uploadTemplatePhoto(files, 'showcase image')
            .pipe(
                catchError((err) => {
                    this.notificationService.notify(
                        this.errorHandlerService.getHttpErrorMessage(err),
                        'Failed uploading showcase image',
                        NotificationType.ERROR);
                    this.isShowcaseImageUploading = false;

                    return of(null);
                }),
                finalize(() => {
                    this.showcaseImageInput.nativeElement.value = '';
                }),
            )
            .subscribe(
                (res) => {
                    if (!this.formGroup.controls.showcaseImageUrls.value) {
                        this.formGroup.controls.showcaseImageUrls.setValue([]);
                    }

                    const imageUrl = res.toString().replace(/^"(.+(?="$))"$/, '$1');
                    this.formGroup.controls.showcaseImageUrls.value.push(imageUrl);
                    this.formGroup.updateValueAndValidity();
                    this.isShowcaseImageUploading = false;
                },
            );
    }

    public removeCoverImage(event): void {
        if (event) {
            event.preventDefault();
            event.stopImmediatePropagation();
        }

        this.coverImageFileName = null;
        this.formGroup.controls.coverImageUrl.setValue('');

        this.coverImageInput.nativeElement.value = '';
    }

    public removeBannerImage(event): void {
        if (event) {
            event.preventDefault();
            event.stopImmediatePropagation();
        }

        this.bannerImageFileName = null;
        this.formGroup.controls.bannerImageUrl.setValue('');

        this.bannerImageInput.nativeElement.value = '';
    }

    public removeShowcaseImage(event, fileName: string): void {
        if (event) {
            event.preventDefault();
            event.stopImmediatePropagation();
        }

        this.showcaseImageFileNames = this.showcaseImageFileNames.filter(item => item !== fileName);
        this.formGroup.controls.showcaseImageUrls.setValue([...this.showcaseImageFileNames]);

        this.showcaseImageInput.nativeElement.value = '';
    }

    public onFormSubmit(event, formValue) {
        event.preventDefault();

        this.template = Object.fromEntries(Object.entries(this.template)
                                             .map(([key, value]) => [key, value === '' ? null : value]));

        if (!formValue.shouldDisplayShowcaseImages) {
            this.template.ShowCaseImg1S3Url = null;
            this.template.ShowCaseImg2S3Url = null;
            this.template.ShowCaseImg3S3Url = null;
        }

        if (this.addNotifyTemplateSubscription) {
            this.addNotifyTemplateSubscription.unsubscribe();
            this.addNotifyTemplateSubscription = null;
        }

        this.isInProgress = true;
        this.addNotifyTemplateSubscription
            = this.notifyTemplateService.addNotifyTemplate(this.template)
                  .pipe(
                      catchError((err) => {
                          this.handleTemplateCreationFailure(err);
                          return of(null);
                      }),
                      finalize(() => {
                          this.isInProgress = false;
                      }),
                  )
                  .subscribe(
                      result => this.handleTemplateCreationSuccess(result),
                  );
    }

    private setupForm(): void {
        this.formGroup = this.formBuilder.group({
            templateTitle: [null, Validators.compose([Validators.required])],
            ppiTemplateName: ['template1'],
            websiteUrl: [null, this.urlValidator()],
            coverImageUrl: [null],
            bannerImageUrl: [null],
            shouldDisplayShowcaseImages: [false],
            showcaseImageUrls: [[]],
            isTermsAgreed: [false, Validators.compose([Validators.requiredTrue])],
        });

        this.monitorValueChanges();
    }

    private isValidUrl(url: string): boolean {
        try {
            new URL(url);
            return true;
        } catch {
            return false;
        }
    }

    private urlOrEmailValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const value = control.value as string;
            if (!value) {
                return null; // consider empty value as valid or add 'Validators.required' if the field is mandatory
            }

            const unformattedValue = value.replace(/^(mailto:|http:\/\/|https:\/\/)/, '');
            const isEmail = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(unformattedValue);
            const isFormattedEmail = isEmail && value.startsWith('mailto:');
            const isUrl = /^(www\.)?[a-z0-9]+([-.][a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/.test(unformattedValue);
            const isFormattedUrl = isUrl && (value.startsWith('http://') || value.startsWith('https://'));

            if (isFormattedEmail || isFormattedUrl) {
                return null; // valid email
            }

            if (isEmail && !isFormattedEmail) {
                return { 'invalidEmailFormat': true }; // invalid email
            }

            if (isUrl && !isFormattedUrl) {
                return { 'invalidUrlFormat': true }; // invalid URL
            }

            return { 'invalidFormat': true }; // neither email nor URL
        };
    }

    private urlValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const value = control.value as string;
            if (!value) {
                return null; // consider empty value as valid or add 'Validators.required' if the field is mandatory
            }

            const unformattedValue = value.replace(/^(http:\/\/|https:\/\/)/, '');
            const isUrl = /^(www\.)?[a-z0-9]+([-.][a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/.test(unformattedValue);
            const isFormattedUrl = isUrl && (value.startsWith('http://') || value.startsWith('https://'));

            if (isFormattedUrl) {
                return null; // valid email
            }

            if (isUrl && !isFormattedUrl) {
                return { 'invalidUrlFormat': true }; // invalid URL
            }

            return { 'invalidFormat': true }; // neither email nor URL
        };
    }

    private monitorValueChanges() {
        this.monitorFormValue();
    }

    private monitorFormValue() {
        this.formGroup.valueChanges
            .subscribe(
                (value) => {
                    this.mapFormValueToTemplate(value);
                    this.formValueChanged.emit(this.template);
                },
            );
    }

    private mapFormValueToTemplate(formValue: any) {
        if (!this.template) {
            this.template = {};
        }

        this.template.TemplateName = formValue.templateTitle;
        this.template.PPITemplateName = formValue.ppiTemplateName;
        this.template.CoverBannerS3Url = formValue.coverImageUrl;
        this.template.BannerS3Url = formValue.bannerImageUrl;
        this.template.shouldDisplayShowcaseImages = formValue.shouldDisplayShowcaseImages;
        this.template.ShowCaseImg1S3Url = formValue.showcaseImageUrls[0];
        this.template.ShowCaseImg2S3Url = formValue.showcaseImageUrls[1];
        this.template.ShowCaseImg3S3Url = formValue.showcaseImageUrls[2];
        this.template.CustomerWebsite = formValue.websiteUrl;

        this.showcaseImageFileNames = [
            formValue.showcaseImageUrls[0],
            formValue.showcaseImageUrls[1],
            formValue.showcaseImageUrls[2],
        ].filter(url => !!url);
    }

    private handleTemplateCreationSuccess(result) {
        if (!result) {
            return;
        }

        this.notificationService.notify('Template has successfully been created.', 'SUCCESS!', NotificationType.SUCCESS);
        this.templateCreated.emit(this.template);
    }

    private handleTemplateCreationFailure(err) {
        this.notificationService.notify(
            this.errorHandlerService.getHttpErrorMessage(err),
            'Failed creating a template',
            NotificationType.ERROR);
    }

    public isLang(lang: string): boolean {
        return this.sessionLanguage === lang;
    }
    
    setTermsOfUseURL() {
        switch(this.appState.user$()?.CountryCode.toUpperCase()) {
            case "GB":
                this.termsOfUseURL = 'https://www.parcelpro.com/gb/en/home.html';
                break;
            case "DE":
                this.termsOfUseURL = 'https://www.parcelpro.com/de/de/home.html'
                break;
            case "FR":
                this.termsOfUseURL = 'https://www.parcelpro.com/fr/fr/home.html'
                break;
            case "IT":
                this.termsOfUseURL = 'https://www.parcelpro.com/it/it/home.html'
                break;
            default:
                this.termsOfUseURL = 'https://www.parcelpro.com/us/en/home.html';
                break;
        }
    }
}
